import { Alert, Box, MenuItem, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { ReviewerPicker } from "components/Submission/ReviewersEditList";
import { SubmissionReasonForSubmittal } from "generated-graphql/graphql";
import { useUpdateSubmission } from "hooks/useSubmissionMutations";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { parseDateLikeToLocalDateTime, toUtcMidnightIsoFromDateLike } from "util/dates";

import { isReasonForSubmittal, reasonForSubmittalOptionsForSite } from "./reasonForSubmittalLabels";

export type SubmissionDetailsAssignee = {
  id: string;
  name: string;
  email: string;
} | null;

type Props = {
  submissionId: string;
  /** Encamp Assignee is an internal staff assignment — only editable by staff. */
  isStaff: boolean;
  /** §1 Reason for Submittal and §18 Public Comments are 8700-12-only fields. */
  isRcra870012: boolean;
  /**
   * The facility has no active EPA ID, so this 8700-12 registers a *new* site.
   * Restricts §1 Reason for Submittal to the two "obtaining an ID" reasons (no
   * discontinuation) and surfaces a notice that a new EPA ID will be requested.
   * Defaults to false (an existing site) when EPA ID presence isn't known.
   */
  isNewSite?: boolean;
  initialDueDate: string | null;
  initialAssignee: SubmissionDetailsAssignee;
  initialNotes: string;
  initialReasonForSubmittal: SubmissionReasonForSubmittal | "";
  onClose: () => void;
};

/**
 * Modal editor for the submission-level Details (due date, Encamp assignee, and
 * the 8700-12 §1/§18 fields). Opened from the pencil on the Details card; the
 * card itself renders the values read-only. Saving persists via
 * `updateSubmission` (which refetches the stepper shell, so the card reflects
 * the new values) and closes the dialog; an unsaved-edit close is confirmed.
 *
 * The title is derived from the facility + form, so it isn't editable here.
 */
export function EditSubmissionDetailsDialog({
  submissionId,
  isStaff,
  isRcra870012,
  isNewSite = false,
  initialDueDate,
  initialAssignee,
  initialNotes,
  initialReasonForSubmittal,
  onClose,
}: Props) {
  const alerts = useAlerts();
  const [updateSubmission, { loading: saving }] = useUpdateSubmission();

  // Due dates are stored as UTC midnight (see `toUtcMidnightIsoFromDateLike`).
  // Parse via `parseDateLikeToLocalDateTime`, which keys off the literal
  // calendar day rather than converting the UTC instant into the browser's
  // local zone — a plain `DateTime.fromISO` would roll the day back in any
  // negative-offset zone, seeding (and re-saving) the date one day early.
  const initialDate = useMemo(
    () => (initialDueDate ? (parseDateLikeToLocalDateTime(initialDueDate) ?? null) : null),
    [initialDueDate]
  );

  const [date, setDate] = useState<DateTime | null>(initialDate);
  const [assignee, setAssignee] = useState<SubmissionDetailsAssignee>(initialAssignee);
  const [notes, setNotes] = useState<string>(initialNotes);
  const [reasonForSubmittal, setReasonForSubmittal] = useState<SubmissionReasonForSubmittal | "">(
    initialReasonForSubmittal
  );

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const isDirty =
    (date?.toISODate() ?? null) !== (initialDate?.toISODate() ?? null) ||
    (assignee?.id ?? null) !== (initialAssignee?.id ?? null) ||
    (isRcra870012 && (notes !== initialNotes || reasonForSubmittal !== initialReasonForSubmittal));

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            dueDate: date ? (toUtcMidnightIsoFromDateLike(date) ?? undefined) : undefined,
            // Encamp Assignee is a staff-only field; customers can't edit it, so
            // only staff include it (the backend rejects it from customers).
            ...(isStaff && { staffAssigneeId: assignee?.id ?? null }),
            ...(isRcra870012 && {
              notes,
              reasonForSubmittal: reasonForSubmittal || null,
            }),
          },
        },
      });
      alerts.success("Saved.");
      onClose();
    } catch (error) {
      alerts.error("Failed to save submission details.", error);
    }
  };

  // Encamp Assignee (flexes to fill) + Due date (fixed compact width) on one
  // row, matching the Details card's read-only layout.
  const rowSx = {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
    alignItems: "flex-start",
  } as const;

  return (
    <>
      <Dialog
        open
        title={<Typography variant="h6">Edit Submission Details</Typography>}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          // A due date is required: block Save when the picker is cleared so we
          // never accept an edit we'd silently drop
          disabled: !isDirty || !date,
          onClick: () => void handleSave(),
        }}
      >
        <Box sx={rowSx}>
          {isStaff && (
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <ReviewerPicker
                staffOnly
                value={assignee ? { ...assignee, order: 0, isStaff: true } : null}
                onChange={(reviewer) =>
                  setAssignee(
                    reviewer
                      ? {
                          id: reviewer.id,
                          name: reviewer.name,
                          email: reviewer.email,
                        }
                      : null
                  )
                }
                inputLabelProps={{ label: "Encamp Assignee", size: "small" }}
              />
            </Box>
          )}
          <DatePicker
            label="Due date"
            value={date}
            onChange={setDate}
            slotProps={{
              textField: { size: "small", sx: { width: 220 }, required: true },
            }}
          />
        </Box>
        {isRcra870012 && (
          <>
            <TextField
              select
              size="small"
              label="Reason for Submittal"
              value={reasonForSubmittal}
              onChange={(e) =>
                setReasonForSubmittal(isReasonForSubmittal(e.target.value) ? e.target.value : "")
              }
              helperText="Appears as Reason for Submittal (§1) on the 8700-12 form."
            >
              {reasonForSubmittalOptionsForSite(!isNewSite).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              multiline
              minRows={3}
              size="small"
              label="Public Comments"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              helperText="Appears as Public Comments (§18) on the 8700-12 form."
            />
            {/* Sits last, directly above the dialog's Save action. */}
            {isNewSite && (
              <Alert severity="info">
                This facility has no EPA ID. Submitting this 8700-12 registers a new site and
                requests a new EPA ID, which must be approved by the EPA or your authorized state
                agency.
              </Alert>
            )}
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
    </>
  );
}
